import { Quaternion as BABYLON_Quaternion, Vector2 as BABYLON_Vector2, Vector3 as BABYLON_Vector3, Vector4 as BABYLON_Vector4, Matrix as BABYLON_Matrix } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { Plane as BABYLON_Plane } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.plane.js';
import { deprecate } from './utils/Deprecated.js';
export const DEG2RAD = Math.PI / 180.0;
export const RAD2DEG = 180.0 / Math.PI;
export const EPSILON_DISTANCE = 0.0001; // This is 10th of a milimeter, which at this time seems like a small enough value.
export const EPSILON_DISTANCE_SQUARED = EPSILON_DISTANCE * EPSILON_DISTANCE;
export const EPSILON_ANGLE_DEGREES = 0.5;
export const EPSILON_ANGLE_RADIANS = EPSILON_ANGLE_DEGREES * DEG2RAD;
export const PARALLEL_DOT = Math.cos(EPSILON_ANGLE_RADIANS); // The precision of this value is subject to change and has been chosen based on observed bahaviour.
export { BABYLON_Vector2 as Vector2 };
export { BABYLON_Vector3 as Vector3 };
export { BABYLON_Vector4 as Vector4 };
export { BABYLON_Quaternion as Quaternion };
export { BABYLON_Matrix as Matrix };
export { BABYLON_Plane as Plane };
class ObjectPool {
    constructor(CONSTRUCTOR, ...args) {
        this.CONSTRUCTOR = CONSTRUCTOR;
        this.pool_storage = [];
        this.pool_size = 100;
        this.pool_storage.length = this.pool_size;
        this._args = args;
        for (let i = 0; i < this.pool_size; i++) {
            this.pool_storage[i] = new this.CONSTRUCTOR(...args);
        }
    }
    fetch() {
        if (this.pool_storage.length === 0) {
            console.warn(`Object pool for ${this.CONSTRUCTOR.name} ran out of objects with a size of ${this.pool_size}`);
            return new this.CONSTRUCTOR(...this._args);
        }
        else {
            // return last object in pool
            return this.pool_storage.pop();
        }
    }
    return(...val) {
        if (this.pool_storage.length > this.pool_size) {
            console.warn(`Object pool for ${this.CONSTRUCTOR.name} has over ${this.pool_size} objects, dropping objects`);
        }
        else {
            for (let i = 0; i < val.length; i++) {
                this.pool_storage.push(val[i]);
            }
        }
    }
}
export const vector2_pool = new ObjectPool(BABYLON_Vector2);
export const vector3_pool = new ObjectPool(BABYLON_Vector3);
export const vector4_pool = new ObjectPool(BABYLON_Vector4);
export const quaternion_pool = new ObjectPool(BABYLON_Quaternion);
export const matrix_pool = new ObjectPool(BABYLON_Matrix);
/**
 * @deprecated
 */
export const vec3 = deprecate(function vec3(x, y, z) {
    return { x: x, y: y, z: z };
}, 'vec3', { new: 'new IVector3(x, y, z)' });
/**
 * @deprecated
 */
export const identity_imatrix4 = deprecate(function identity_imatrix4() {
    return [
        { x: 1, y: 0, z: 0, w: 0 },
        { x: 0, y: 1, z: 0, w: 0 },
        { x: 0, y: 0, z: 1, w: 0 },
        { x: 0, y: 0, z: 0, w: 1 }
    ];
}, 'identity_imatrix4', { new: 'IMatrix4.Identity()' });
/**
 * @deprecated
 * Return a "pure diagonal" vector (vector with equal elements).
 * https://math.stackexchange.com/questions/1626821/what-is-the-name-of-a-vector-with-all-equal-elements
 * @param magnitude The magnitude of each element
 */
export const pure_diagonal_vector = deprecate(function pure_diagonal_vector(magnitude) {
    return vec3(magnitude, magnitude, magnitude);
}, 'pure_diagonal_vector', { new: 'new IVector3(value)' });
/**
 * @deprecated
 */
export const add = deprecate(function add(a, b) {
    return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}, 'add', { new: 'IVector3.add(a, b)' });
/**
 * @deprecated
 */
export const sub = deprecate(function sub(a, b) {
    return vec3(a.x - b.x, a.y - b.y, a.z - b.z);
}, 'sub', { new: 'IVector3.subtract(a, b)' });
/**
 * @deprecated
 * Creates new matrix from a list of values (16)
 * @param initial_m11 defines 1st value of 1st row
 * @param initial_m12 defines 2nd value of 1st row
 * @param initial_m13 defines 3rd value of 1st row
 * @param initial_m14 defines 4th value of 1st row
 * @param initial_m21 defines 1st value of 2nd row
 * @param initial_m22 defines 2nd value of 2nd row
 * @param initial_m23 defines 3rd value of 2nd row
 * @param initial_m24 defines 4th value of 2nd row
 * @param initial_m31 defines 1st value of 3rd row
 * @param initial_m32 defines 2nd value of 3rd row
 * @param initial_m33 defines 3rd value of 3rd row
 * @param initial_m34 defines 4th value of 3rd row
 * @param initial_m41 defines 1st value of 4th row
 * @param initial_m42 defines 2nd value of 4th row
 * @param initial_m43 defines 3rd value of 4th row
 * @param initial_m44 defines 4th value of 4th row
 * @returns the new matrix
 */
export const from_values_to_matrix4 = deprecate(function from_values_to_matrix4(initial_m11, initial_m12, initial_m13, initial_m14, initial_m21, initial_m22, initial_m23, initial_m24, initial_m31, initial_m32, initial_m33, initial_m34, initial_m41, initial_m42, initial_m43, initial_m44) {
    const matrix = [
        { x: initial_m11, y: initial_m12, z: initial_m13, w: initial_m14 },
        { x: initial_m21, y: initial_m22, z: initial_m23, w: initial_m24 },
        { x: initial_m31, y: initial_m32, z: initial_m33, w: initial_m34 },
        { x: initial_m41, y: initial_m42, z: initial_m43, w: initial_m44 }
    ];
    return matrix;
}, 'from_values_to_matrix4', { new: 'IMatrix4.from_values(...vals)' });
// Implementation taken from BabylonJS
// https://github.com/BabylonJS/Babylon.js/blob/86928af69d738d9a66c2e2468f0a2fe301f86780/packages/dev/core/src/Maths/math.vector.ts#L4705
/**
 * @deprecated
 */
export const inverse = deprecate(function inverse(m) {
    // TODO: Add early out for identity matrix
    // the inverse of a Matrix is the transpose of cofactor matrix divided by the determinant
    const m00 = m[0].x;
    const m01 = m[0].y;
    const m02 = m[0].z;
    const m03 = m[0].w;
    const m10 = m[1].x;
    const m11 = m[1].y;
    const m12 = m[1].z;
    const m13 = m[1].w;
    const m20 = m[2].x;
    const m21 = m[2].y;
    const m22 = m[2].z;
    const m23 = m[2].w;
    const m30 = m[3].x;
    const m31 = m[3].y;
    const m32 = m[3].z;
    const m33 = m[3].w;
    const det_22_33 = m22 * m33 - m32 * m23;
    const det_21_33 = m21 * m33 - m31 * m23;
    const det_21_32 = m21 * m32 - m31 * m22;
    const det_20_33 = m20 * m33 - m30 * m23;
    const det_20_32 = m20 * m32 - m22 * m30;
    const det_20_31 = m20 * m31 - m30 * m21;
    const cofact_00 = +(m11 * det_22_33 - m12 * det_21_33 + m13 * det_21_32);
    const cofact_01 = -(m10 * det_22_33 - m12 * det_20_33 + m13 * det_20_32);
    const cofact_02 = +(m10 * det_21_33 - m11 * det_20_33 + m13 * det_20_31);
    const cofact_03 = -(m10 * det_21_32 - m11 * det_20_32 + m12 * det_20_31);
    const det = m00 * cofact_00 + m01 * cofact_01 + m02 * cofact_02 + m03 * cofact_03;
    if (det === 0) {
        console.warn('Matrix determinant is zero so it could not be inverted');
        return m;
    }
    const det_inv = 1 / det;
    const det_12_33 = m12 * m33 - m32 * m13;
    const det_11_33 = m11 * m33 - m31 * m13;
    const det_11_32 = m11 * m32 - m31 * m12;
    const det_10_33 = m10 * m33 - m30 * m13;
    const det_10_32 = m10 * m32 - m30 * m12;
    const det_10_31 = m10 * m31 - m30 * m11;
    const det_12_23 = m12 * m23 - m22 * m13;
    const det_11_23 = m11 * m23 - m21 * m13;
    const det_11_22 = m11 * m22 - m21 * m12;
    const det_10_23 = m10 * m23 - m20 * m13;
    const det_10_22 = m10 * m22 - m20 * m12;
    const det_10_21 = m10 * m21 - m20 * m11;
    const cofact_10 = -(m01 * det_22_33 - m02 * det_21_33 + m03 * det_21_32);
    const cofact_11 = +(m00 * det_22_33 - m02 * det_20_33 + m03 * det_20_32);
    const cofact_12 = -(m00 * det_21_33 - m01 * det_20_33 + m03 * det_20_31);
    const cofact_13 = +(m00 * det_21_32 - m01 * det_20_32 + m02 * det_20_31);
    const cofact_20 = +(m01 * det_12_33 - m02 * det_11_33 + m03 * det_11_32);
    const cofact_21 = -(m00 * det_12_33 - m02 * det_10_33 + m03 * det_10_32);
    const cofact_22 = +(m00 * det_11_33 - m01 * det_10_33 + m03 * det_10_31);
    const cofact_23 = -(m00 * det_11_32 - m01 * det_10_32 + m02 * det_10_31);
    const cofact_30 = -(m01 * det_12_23 - m02 * det_11_23 + m03 * det_11_22);
    const cofact_31 = +(m00 * det_12_23 - m02 * det_10_23 + m03 * det_10_22);
    const cofact_32 = -(m00 * det_11_23 - m01 * det_10_23 + m03 * det_10_21);
    const cofact_33 = +(m00 * det_11_22 - m01 * det_10_22 + m02 * det_10_21);
    return from_values_to_matrix4(cofact_00 * det_inv, cofact_10 * det_inv, cofact_20 * det_inv, cofact_30 * det_inv, cofact_01 * det_inv, cofact_11 * det_inv, cofact_21 * det_inv, cofact_31 * det_inv, cofact_02 * det_inv, cofact_12 * det_inv, cofact_22 * det_inv, cofact_32 * det_inv, cofact_03 * det_inv, cofact_13 * det_inv, cofact_23 * det_inv, cofact_33 * det_inv);
}, 'inverse', { new: 'IMatrix4.inverse(m)' });
export function determinant3x3(m) {
    if (m.length !== 16)
        throw new Error('determinant3x3 expects an array of length 16 (4x4)');
    const detA = (m[4 * 1 + 1] * m[4 * 2 + 2] - m[4 * 1 + 2] * m[4 * 2 + 1]);
    const detB = (m[4 * 1 + 2] * m[4 * 2 + 0] - m[4 * 1 + 0] * m[4 * 2 + 2]);
    const detC = (m[4 * 1 + 0] * m[4 * 2 + 1] - m[4 * 1 + 1] * m[4 * 2 + 0]);
    return detA * m[4 * 0 + 0] + detB * m[4 * 0 + 1] + detC * m[4 * 0 + 2];
}
export function imatrix4_equals_babylon_matrix(imatrix, bmatrix) {
    // Because babylon actually uses a Float32Array as their backing storage for their matrices, we need to compare in their float space.
    // So we convert the viewer matrix to a babylon matrix first, and then compare that with the given babylon matrix.
    const temporary_compare_babylon_matrix = matrix_pool.fetch().fromIMatrix4(imatrix);
    const result = temporary_compare_babylon_matrix.equals(bmatrix);
    matrix_pool.return(temporary_compare_babylon_matrix);
    return result;
}
/**
 * @deprecated
 */
export const copy_imatrix4_to_ref = deprecate(function copy_imatrix4_to_ref(src, dst) {
    for (let i = 0; i < 4; i++) {
        dst[i].x = src[i].x;
        dst[i].y = src[i].y;
        dst[i].z = src[i].z;
        dst[i].w = src[i].w;
    }
}, 'copy_imatrix4_to_ref', { new: 'IMatrix4.copy_to_ref(a, b)' });
export function horizontal_to_vertical_fov(horizontal_fov, aspect_ratio) {
    return 2 * Math.atan(Math.tan(horizontal_fov * 0.5) / aspect_ratio);
}
export function vertical_to_horizontal_fov(vertical_fov, aspect_ratio) {
    return 2 * Math.atan(Math.tan(vertical_fov * 0.5) * aspect_ratio);
}
export function clamp(a, min, max) {
    return Math.min(Math.max(a, min), max);
}
function instance_of_i_color(object) {
    return 'r' in object;
}
/**
 * @deprecated
 * Returns an object with numbers r, g and b, representing respective colors.
 */
export const color3 = deprecate(function color3(r = 0, g = 0, b = 0) {
    return { r: r, g: g, b: b };
}, 'color3', { new: 'new IColor(r, g, b)' });
/**
 * @deprecated
 * Returns an IColor object with integers in the range 0 - 255
 * @param c An IColor object with floats in the range 0.0 - 1.0
 */
export const color_float_to_uint = deprecate(function color_float_to_uint(c) {
    return color3(c.r * 255, c.g * 255, c.b * 255);
}, 'color_float_to_uint');
/**
 * @deprecated
 * Returns an IColor object with floats in the range 0.0 - 1.0
 * @param c An IColor object with integers in the range 0 - 255
 */
export const color_uint_to_float = deprecate(function color_uint_to_float(c) {
    return color3(c.r / 255, c.g / 255, c.b / 255);
}, 'color_uint_to_float');
function ensure_i_vector(vec_or_color) {
    let vec_1;
    if (instance_of_i_color(vec_or_color)) {
        vec_1 = { x: vec_or_color.r, y: vec_or_color.g, z: vec_or_color.b };
    }
    else {
        vec_1 = vec_or_color;
    }
    return vec_1;
}
/**
 * @deprecated
 */
export const lerp = deprecate(function lerp(a, b, scale) {
    if (scale < 0.0 || scale > 1.0) {
        console.warn('Keep scale between 0.0 and 1.0');
        scale = scale % 1.0;
    }
    a = ensure_i_vector(a);
    b = ensure_i_vector(b);
    return vec3(a.x + scale * (b.x - a.x), a.y + scale * (b.y - a.y), a.z + scale * (b.z - a.z));
}, 'lerp', { new: 'IVector3.lerp(v)' });
export function lerp_float(a, b, scale) {
    return a * (1 - scale) + b * scale;
}
function dep_length(v) {
    return Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
}
/**
 * @deprecated
 */
export const length = deprecate(dep_length, 'length', { new: 'IVector3.magnitude(v)' });
function dep_dist_squared(a, b) {
    const v = sub(a, b);
    return v.x * v.x + v.y * v.y + v.z * v.z;
}
/**
 * @deprecated
 */
export const dist_squared = deprecate(dep_dist_squared, 'dist_squared', { new: 'IVector3.sqr_distance(v)' });
/**
 * @deprecated
 * Returns a vector multiplied by a scalar.
 * @param a the vector.
 * @param s the scalar.
 */
export const muls = deprecate(function muls(a, s) {
    return vec3(a.x * s, a.y * s, a.z * s);
}, 'muls', { new: 'IVector3.scale(v, scale)' });
/**
 * @deprecated
 * Returns a vector divided by a scalar.
 * @param v the vector.
 * @param s the scalar.
 */
export const divs = deprecate(function divs(v, s) {
    return { x: v.x / s, y: v.y / s, z: v.z / s };
}, 'divs');
/**
 * @deprecated
 */
export const normalize = deprecate(function normalize(v) {
    return divs(v, length(v));
}, 'normalize', { new: 'IVector3.normalize(v)' });
/**
 * @deprecated
 */
export const dot = deprecate(function dot(a, b) {
    return a.x * b.x + a.y * b.y + a.z * b.z;
}, 'dot', { new: 'IVector3.dot(a, b)' });
/**
 * @deprecated
 */
export const angle = deprecate(function angle(a, b) {
    return Math.acos(dot(a, b) / (length(a) * length(b)));
}, 'angle', { new: 'IVector3.angle(a, b)' });
/**
 * Returns the solid angle of an angle.
 * @param a an angle measured in radians
 * @returns solid angle measured in steradians
 */
export function solid_angle(a) {
    // 2*pi*(1-cos(a/2))
    return 2 * Math.PI * (1 - Math.cos(a / 2));
}
/**
 * @deprecated
 */
export const max_component = deprecate(function max_component(v) {
    return v.x >= v.y && v.x >= v.z ? v.x : v.y >= v.z ? v.y : v.z;
}, 'max_component', { new: 'IVector3.max_component(v)' });
/**
 * @deprecated
 */
export const absv3 = deprecate(function absv3(v) {
    return vec3(Math.abs(v.x), Math.abs(v.y), Math.abs(v.z));
}, 'absv3');
/**
 * @deprecated
 */
export const min_vector3_to_ref = deprecate(function min_vector3_to_ref(a, b, ref) {
    ref.x = Math.min(a.x, b.x);
    ref.y = Math.min(a.y, b.y);
    ref.z = Math.min(a.z, b.z);
}, 'min_vector3_to_ref', { new: 'IVector3.min_to_ref(a, b, r)' });
/**
 * @deprecated
 */
export const max_vector3_to_ref = deprecate(function max_vector3_to_ref(a, b, ref) {
    ref.x = Math.max(a.x, b.x);
    ref.y = Math.max(a.y, b.y);
    ref.z = Math.max(a.z, b.z);
}, 'max_vector3_to_ref', { new: 'IVector3.max_to_ref(a, b, r)' });
/**
 * @deprecated
 */
export const perpendicular = deprecate(function perpendicular(v) {
    if (Math.abs(v.y) < Math.abs(v.x)) {
        return vec3(v.z, 0, -v.x);
    }
    else {
        return vec3(0, v.z, -v.y);
    }
}, 'perpendicular', { new: 'IVector3.perpendicular(v)' });
/**
 * @deprecated
 */
export const cross = deprecate(function cross(a, b) {
    return vec3(a.y * b.z - a.z * b.y, a.z * b.x - a.x * b.z, a.x * b.y - a.y * b.x);
}, 'cross', { new: 'IVector3.cross(a, b)' });
/**
 * Rotate a vector.
 */
export function rotate(vector3, delta_angle, rotation_axis) {
    // TODO: Fix new once merged to main
    const quaternion = new BABYLON_Quaternion();
    BABYLON_Quaternion.RotationAxisToRef(new BABYLON_Vector3(rotation_axis.x, rotation_axis.y, rotation_axis.z), delta_angle, quaternion);
    const babylon_vector = new BABYLON_Vector3(vector3.x, vector3.y, vector3.z)
        .rotateByQuaternionToRef(quaternion, new BABYLON_Vector3(0.0, 0.0, 0.0));
    return { x: babylon_vector.x, y: babylon_vector.y, z: babylon_vector.z };
}
export function radians(degrees) {
    return Math.PI * degrees / 180;
}
export function degrees(radians) {
    return 180 * radians / Math.PI;
}
export class TrackedVec3 {
    constructor(x = 0.0, y = 0.0, z = 0.0) {
        this._x = 0;
        this._y = 0;
        this._z = 0;
        this._x = x;
        this._y = y;
        this._z = z;
    }
    report_change() {
        if (this.onchange) {
            this.onchange();
        }
    }
    get x() {
        return this._x;
    }
    set x(v) {
        if (this._x === v)
            return;
        this._x = v;
        this.report_change();
    }
    get y() {
        return this._y;
    }
    set y(v) {
        if (this._y === v)
            return;
        this._y = v;
        this.report_change();
    }
    get z() {
        return this._z;
    }
    set z(v) {
        if (this._z === v)
            return;
        this._z = v;
        this.report_change();
    }
    get r() {
        return this._x;
    }
    set r(v) {
        if (this._x === v)
            return;
        this._x = v;
        this.report_change();
    }
    get g() {
        return this._y;
    }
    set g(v) {
        if (this._y === v)
            return;
        this._y = v;
        this.report_change();
    }
    get b() {
        return this._z;
    }
    set b(v) {
        if (this._z === v)
            return;
        this._z = v;
        this.report_change();
    }
    set(x, y, z) {
        if (this._x !== x || this._y !== y || this._z !== z) {
            this._x = x;
            this._y = y;
            this._z = z;
            this.report_change();
        }
    }
    copy(v) {
        this.set(v.x, v.y, v.z);
    }
}
/**
 * @deprecated
 * Check if two vector3s are equivalent.
 * @param lh first vector
 * @param rh second vector
 */
export const equivalent_vector3s = deprecate(function equivalent_vector3s(lh, rh) {
    return lh.x === rh.x && lh.y === rh.y && lh.z === rh.z;
}, 'equivalent_vector3s', { new: 'IVector3.equals(a, b)' });
/**
 * @deprecated
 * Check if two vector4s are equivalent.
 * @param lh first vector
 * @param rh second vector
 */
export const equivalent_vector4s = deprecate(function equivalent_vector4s(lh, rh) {
    return lh.x === rh.x && lh.y === rh.y && lh.z === rh.z && lh.w === rh.w;
}, 'equivalent_vector4s', { new: 'IVector4.equals(a, b)' });
/**
 * @deprecated
 * Check if two matrix4s are equivalent.
 * @param lh first matrix
 * @param rh second matrix
 */
export const equivalent_matrix4s = deprecate(function equivalent_matrix4s(lh, rh) {
    return equivalent_vector4s(lh[0], rh[0]) &&
        equivalent_vector4s(lh[1], rh[1]) &&
        equivalent_vector4s(lh[2], rh[2]) &&
        equivalent_vector4s(lh[3], rh[3]);
}, 'equivalent_matrix4s', { new: 'IMatrix4.equals(a, b)' });
/**
 * @deprecated
 */
export const from_array_to_matrix4 = deprecate(function from_array_to_matrix4(val) {
    const matrix = [
        { x: val[0], y: val[1], z: val[2], w: val[3] },
        { x: val[4], y: val[5], z: val[6], w: val[7] },
        { x: val[8], y: val[9], z: val[10], w: val[11] },
        { x: val[12], y: val[13], z: val[14], w: val[15] }
    ];
    return matrix;
}, 'from_array_to_matrix4', { new: 'IMatrix4.from_array(arr)' });
/**
 * @deprecated
 * Project a vector3 onto a plane and return resulting vector3.
 * @param v a vector3
 * @param plane_normal normal of the plane
 */
export const project_onto_plane = deprecate(function project_onto_plane(v, plane_normal) {
    const ortho_component = muls(plane_normal, dot(v, plane_normal));
    return sub(v, ortho_component); // Project by removing the component of v orthogonal to the plane
}, 'project_onto_plane', { new: 'IVector3.project_on_plane(v, p)' });
export function get_euler_angles_from_rotation_matrix(transform) {
    const matrix = matrix_pool.fetch().fromIMatrix4(transform);
    const rotation_matrix = matrix.getRotationMatrixToRef(matrix_pool.fetch());
    const quaternion = new BABYLON_Quaternion();
    quaternion.fromRotationMatrix(rotation_matrix);
    matrix_pool.return(matrix, rotation_matrix);
    return quaternion.toEulerAngles();
}
//# sourceMappingURL=Math.js.map